import {
  OperationVariables,
  TypedDocumentNode,
  useQuery,
} from "@apollo/client";
import gqltag from "graphql-tag";
import { gql } from "_graphql-types-frontend";
/*
  This tag is used to avoid detection by graphql type processing 
  and acknowledge dynamic gql usage.
  Usage of dynamic gql goes against graphql convention.
  Usage should be avoided, graphql has some support
  for work arounds for dynamic gql using directives, which should be 
  preferred when possible. Be careful.
*/
export const dynamicgql = gqltag;

export function useQueryWithSkip<T, V extends OperationVariables>(
  query: TypedDocumentNode<T, V>,
  variables: V | undefined
) {
  return useQuery<T, V>(query, { variables, skip: variables === undefined });
}

export const USER_SETTINGS = gql(`
  fragment UserSettingsAttributes on UserSettings {
    id
    data {
      defaultInvestmentView {
        type
        id
      }
    }
  }
`);

export const CURRENT_USER_QUERY = gql(/* GraphQL */ `
  query currentUser {
    currentUser {
      id
      organizationId
      sub
      username
      userName
      commonName
      email
      imageType
      imageTypeSm
      entryTypeId
      flags {
        ...UserFlagList
      }
      userSettings {
        ...UserSettingsAttributes
      }
      organization {
        name
        id
      }
      currentUserId
    }
  }
`);

export const USER_FLAGS = gql(/* GraphQL */ `
  fragment UserFlagList on UserFlags {
    DCAdmin
    isRockCreek
    isSubscriptionUser
    subscriberDataAccess
    isDataManager
    anyUser
    businessApplicant
    isSuperUser
    RECFReviewer
    canAssumeUser
    canSiriusChat
    isClientUser
    isPortalUser
    investmentPageAccess
    coreUser
    isEditPrimary
    isComplianceUser
  }
`);

export const INVESTMENT_PROFILE_FRAGMENT = gql(`
fragment InvestmentProfileFragment on GeneratedInvestmentProfile {
  name
  firmId
  firmName
  inceptionDate
  isImpact
  summary
  marketEnumId
  vehicleTypeEnumId
  assetClassEnumId
  strategyEnumId
  peStrategyEnumId
  primaryGeographicFocusId
  primaryBenchmarkId
  secondaryBenchmarkId
  currencyDenominationEnumId
  vintageYear
  peLegalLifeYrs
  gpCommitted
  committedCapital
  targetRaise
  minTargetNetMOIC
  minTargetNetIRR
  maxTargetNetMOIC
  maxTargetNetIRR
}
`);

export const FIM_PROFILE_FRAGMENT = gql(`
  fragment FirmProfileFragment on GeneratedFirmProfile {
    name
    yearFounded
    cik
    lei
    crd
    firmIrEmail
    registeredWithIds
    primaryAddress {
      line1
      line2
      line3
      city
      countryEnumId
      stateEnumId
      zipCode
      website
      phone
      phone2
      fax
      fax2
    }
    employeeTotal
    portfolioManagerCount
    investmentCount
    analystCount
    traderCount
    operationsCount
    administrativeCount
    investorRelationsCount
    legalCount
  }
`);

export const SHARE_CLASS_PROFILE_FRAGMENT = gql(`
  fragment ShareClassProfileFragment on GeneratedShareClassProfile {
    name
    status
    restricted
    isRcgNegotiated
    FAS157
    currencyDenominationEnumId
    otherFees
    investmentFees {
      mgmtFee
      mgmtFeeCalcEnumId
      mgmtFeeFreqEnumId
      incentiveFee
      incentiveFeeCalcEnumId
      incentiveFeeFreqEnumId
      otherFee
      otherFeeCalcEnumId
      otherFeeFreqEnumId
      highWaterMark
      annualReset
      hurdle
      hurdleBenchmark
      standardShareClassRates
      feeStructureChanged
      feeStructureChangedNotes
      abnormalFees
      abnormalFeesNotes
      peMgmtFeeCalcEnumId
      peIncentiveFeeCalcEnumId
      peMgmtFeeDuringCommitment
      peMgmtFeeDuringCommitmentCalcEnumId
      peMgmtFeeDuringHarvest
      peMgmtFeeDuringHarvestCalcEnumId
      hasMgmtFeeStepDown
      peMgmtFeeStepDownPercent
      peMgmtFeeStepDownCalcEnumId
    }
    liquidity {
      hasGate
      gatePercentNav
      gatePercentCapital
      exceptions
      hasRollover
      rolloverValue
      rolloverUnitId
      hasLockup
      lockupValue
      lockupUnitId
      lockupEndValueId
      lockupEndBizDays
      secondLockupEndValueId
      secondLockupEndBizDays
      lockupMonthlyValue
      subscriptionValue
      subscriptionUnitId
      liquidityValue
      liquidityUnitId
      liquidityMonthlyValue
      cashAvailableDays
      noticeValue
      noticeUnitId
      noticeBizDays
      hasHoldback
      holdbackPercent
      workoutId
      modelingNotes
    }
    peLiquidity {
      preferredReturn
      isCompounded
      compoundingPeriodId
      hasCatchupProvision
      catchupPercent
      optionOfExtensionId
    }
  }
`);

export const ADD_TEMP_FILES = gql(`
  mutation AddTempFiles($input: [TempFileUpload!]!) {
    addTempFiles(input: $input) {
      key
      bucket
      filename
      suggestions {
        documentTypeEnumId
        date
      }
    }
  }
`);

export const BUILD_INVESTMENT_PROFILE = gql(`
  query ProfileBuilder($keys: [String!]!, $bucket: String!) {
    investmentProfileBuilder(
      Keys: $keys
      bucket: $bucket
    ) {
      answer
      conversationId
      investmentProfile {
        ...InvestmentProfileFragment
      }
    }
  }
`);

export const BUILD_FIRM_PROFILE = gql(`
  query FirmProfileFromUrlBuilder($url: String!, $firmId: Int) {
    firmProfileFromUrlBuilder(url: $url, firmId: $firmId) {
      answer
      conversationId
      firmProfile {
        ...FirmProfileFragment
        }
      }
    }
`);

export const BUILD_SHARE_CLASS_PROFILE = gql(`
  query ShareClassProfileBuilder($keys: [String!]!, $bucket: String!, $investmentId: Int!) {
    shareClassProfileBuilder(
      Keys: $keys
      bucket: $bucket
      investmentId: $investmentId
    ) {
      answer
      conversationId
      shareClassProfiles {
        ...ShareClassProfileFragment
      }
    }
  }
`);

export const PERSON_PROFILE_FRAGMENT = gql(`
  fragment PersonProfileFragment on GeneratedPersonProfile {
    firstName
    middleName
    lastName
    phone
    phone2
    fax
    fax2
    email
    email2
    isCFA
    biography
    titleEnumId
    educationSummary
    pronouns
    workExperienceSummary
    comments
    linkedin
    twitter
    firmEmployment {
      firmId
      firmName
      employeeRoleEnumId
      jobDescription
      startDate
      isFounder
      isKeyPersonnel
    }
    diversity {
      africanAmerican
      asian
      hispanic
      lgbtq
      nativeAmerican
      other
      veteran
      disabled
      woman
      nonBinary
      hawaiianOrPacificIslander
    }
  }
`);

export const BUILD_PERSON_PROFILE_FROM_URL = gql(`
  query PersonProfileFromUrlBuilder($url: String!, $personId: Int) {
    personProfileFromUrlBuilder(url: $url, personId: $personId) {
      answer
      conversationId
      personProfile {
        ...PersonProfileFragment
      }
    }
  }
`);

export const BUILD_PERSON_PROFILE_FROM_FILES = gql(`
  query PersonProfileFromFilesBuilder($keys: [String!]!, $bucket: String!) {
    personProfileFromFilesBuilder(
      keys: $keys
      bucket: $bucket
    ) {
      answer
      conversationId
      personProfile {
        ...PersonProfileFragment
      }
    }
  }
`);

export const BULK_PERSON_PROFILE_FROM_FILES = gql(`
  query BulkPersonProfileFromFilesBuilder($keys: [String!]!, $bucket: String!, $firmId: Int, $conversationId: Int) {
    bulkPersonProfileFromFilesBuilder(
      keys: $keys
      bucket: $bucket
      firmId: $firmId
      conversationId: $conversationId
    ) {
      answer
      conversationId
      personProfiles {
        ...PersonProfileFragment
      }
    }
  }
`);

export const ADD_USER_ACTIITY = gql(`
mutation AddUserActivity($input: UserActivityInput!) {
  addUserActivity(input: $input) {
    id
  }
}`);
